import React, { useState, useRef, useCallback } from "react";

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = "Før",
  afterLabel = "Efter",
  title,
  description,
  onOpenLightbox,
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    let percentage = (x / rect.width) * 100;
    if (percentage < 0) percentage = 0;
    if (percentage > 100) percentage = 100;
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches && e.touches[0]) {
        updatePosition(e.touches[0].clientX);
      }
    },
    [updatePosition]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        updatePosition(e.clientX);
      }
    },
    [isDragging, updatePosition]
  );

  const handlePointerDown = (e) => {
    setIsDragging(true);
    updatePosition(e.clientX || (e.touches && e.touches[0]?.clientX));
  };

  const handlePointerUp = () => setIsDragging(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
      {/* Slider Viewport */}
      <div
        ref={containerRef}
        className="relative w-full aspect-4/3 overflow-hidden select-none cursor-ew-resize bg-gray-100 group"
        onMouseMove={handleMouseMove}
        onMouseDown={handlePointerDown}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchMove={handleTouchMove}
        onTouchStart={handlePointerDown}
        onTouchEnd={handlePointerUp}
      >
        {/* After Image (Background) */}
        <img
          src={afterImage}
          alt="Efter"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          loading="lazy"
        />
        <div className="absolute top-3 right-3 z-10 pointer-events-none">
          <span className="px-2.5 py-1 bg-black/60 backdrop-blur-xs text-white font-semibold text-xs rounded shadow-xs">
            {afterLabel}
          </span>
        </div>

        {/* Before Image (Clipped Overlay) */}
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            src={beforeImage}
            alt="Før"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 z-10">
            <span className="px-2.5 py-1 bg-[#c06228] text-white font-semibold text-xs rounded shadow-xs">
              {beforeLabel}
            </span>
          </div>
        </div>

        {/* Divider Line & Handle */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_6px_rgba(0,0,0,0.4)] z-20 pointer-events-none"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white text-[#1e2329] flex items-center justify-center shadow-md border border-gray-200">
            <i className="fa-solid fa-arrows-left-right text-xs text-[#c06228]" aria-hidden="true" />
          </div>
        </div>

        {/* Lightbox Button */}
        {onOpenLightbox && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenLightbox();
            }}
            className="absolute bottom-3 right-3 z-20 bg-black/60 hover:bg-black/80 text-white p-2 rounded text-xs font-medium backdrop-blur-xs transition-opacity opacity-80 hover:opacity-100 flex items-center gap-1.5"
            aria-label="Forstør billede"
          >
            <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" />
            <span className="hidden sm:inline">Forstør</span>
          </button>
        )}
      </div>

      {/* Info footer */}
      {(title || description) && (
        <div className="p-4 flex items-center justify-between border-t border-gray-100 bg-white">
          <div>
            {title && <h3 className="text-sm sm:text-base font-bold text-[#1e2329]">{title}</h3>}
            {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
          </div>
          <span className="text-[11px] text-gray-400 font-medium shrink-0 ml-3 hidden sm:inline">
            <i className="fa-solid fa-arrows-left-right mr-1 text-[#c06228]" aria-hidden="true" />
            Træk i skyderen
          </span>
        </div>
      )}
    </div>
  );
}
